import { useQuery } from '@tanstack/react-query';
import { Alerte, Vide } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { dateComplete } from './dates';
import { libelleSchema, type VersionSchema } from './types';

/**
 * Historique des versions du schéma de garde.
 *
 * C'est ce qui permet de répondre à « quel était le schéma en mars dernier ? » deux ans
 * plus tard, et ce qui donne sa valeur au dossier remis à un avocat. Chaque version
 * indique ses dates de validité ; aucune n'est effacée.
 */
export function HistoriqueSchema({ caseFileId }: { caseFileId: string }) {
  const { client } = useSession();

  const versions = useQuery({
    queryKey: ['schedule-history', caseFileId],
    queryFn: () =>
      client.request<VersionSchema[]>(`/case-files/${caseFileId}/custody/schedule/history`),
  });

  if (versions.isPending) return <p className="discret">Chargement de l'historique…</p>;

  if (versions.isError) {
    return (
      <Alerte titre="L’historique n’a pas pu être chargé">{messageDErreur(versions.error)}</Alerte>
    );
  }

  if (versions.data.length === 0) {
    return <Vide categorie="calendrier">Aucun schéma de garde n'est encore défini.</Vide>;
  }

  return (
    <ol style={{ paddingLeft: 'var(--espace-lg)' }}>
      {[...versions.data].reverse().map((version) => (
        <li key={version.id} style={{ marginBottom: 'var(--espace-md)' }}>
          <strong>{libelleSchema(version.kind, { nidation: version.params.nesting === true })}</strong>
          <br />
          <span className="discret">
            Version {version.version} · en vigueur depuis le{' '}
            {dateComplete(new Date(version.validFrom))}
            {version.supersededAt
              ? `, remplacée le ${dateComplete(new Date(version.supersededAt))}`
              : ' — schéma actuel'}
          </span>
          <br />
          <span className="discret">
            {version.createdByProposalId
              ? 'Issue d’un accord entre les deux parents.'
              : 'Schéma initial, choisi à la création du dossier.'}
          </span>
        </li>
      ))}
    </ol>
  );
}
